import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  MapPin,
  Calendar,
  ArrowLeft,
  Building2,
  CheckCircle2,
  Ruler,
  Users,
  Clock,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { useCMS } from "@/context/CMSContext";

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function getProjectServices(services: unknown) {
  if (Array.isArray(services)) {
    return services.filter((service): service is string => typeof service === "string" && service.trim().length > 0);
  }

  if (typeof services === "string") {
    try {
      const parsed = JSON.parse(services);
      if (Array.isArray(parsed)) {
        return parsed.filter((service): service is string => typeof service === "string" && service.trim().length > 0);
      }
    } catch {
      return services
        .split(",")
        .map((service) => service.trim())
        .filter(Boolean);
    }
  }

  return [];
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { projects, isLoading } = useCMS();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const project = projects.find((p) => p.id.toString() === id && p.status !== "Draft");
  const projectServices = getProjectServices(project?.services);

  // Combine feature image and gallery images
  const allImages = project
    ? [
        project.image_url,
        ...(project.gallery_images || []),
      ].filter(Boolean)
    : [];

  if (isLoading) {
    return (
      <main className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-accent rounded-full animate-spin mx-auto" />
          <p className="text-neutral-500 mt-4">Loading project...</p>
        </div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="min-h-screen pt-28 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-neutral-400" />
          </div>
          <h1 className="text-2xl font-bold text-primary mb-2">Project Not Found</h1>
          <p className="text-neutral-500 mb-6">
            The project you're looking for doesn't exist.
          </p>
          <Link
            to="/projects"
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full hover:bg-accent-dark transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Link>
        </div>
      </main>
    );
  }

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-primary via-primary to-primary-light relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(219,106,64,0.25) 0%, transparent 70%)",
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0.8, 0.5],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <motion.nav
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex items-center gap-2 text-sm mb-4"
            >
              <Link to="/" className="text-white/60 hover:text-accent transition-colors">
                Home
              </Link>
              <span className="text-white/40">/</span>
              <Link
                to="/projects"
                className="text-white/60 hover:text-accent transition-colors"
              >
                Projects
              </Link>
              <span className="text-white/40">/</span>
              <span className="text-accent font-medium">{project.title}</span>
            </motion.nav>

            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-accent font-medium text-xs mb-4"
            >
              <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
              {project.category}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 max-w-3xl"
            >
              {project.title}
            </motion.h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="w-16 h-0.5 bg-accent origin-left mb-6 rounded-full"
            />

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 text-white/70 text-sm"
            >
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {project.location}
              </div>
              <span className="text-white/30">|</span>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {project.year}
              </div>
              <span className="text-white/30">|</span>
              <div className="flex items-center gap-1">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  project.status === "Completed"
                    ? "bg-green-500 text-white"
                    : "bg-amber-500 text-white"
                }`}>
                  {project.status}
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Image & Gallery */}
            <div className="lg:col-span-2 space-y-8">
              {/* Main Image */}
              <AnimatedSection>
                <div 
                  className="relative h-80 md:h-96 rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                  onClick={() => openLightbox(0)}
                >
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
                  <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs text-neutral-600">
                    Click to view gallery
                  </div>
                </div>
              </AnimatedSection>

              {/* Gallery Grid */}
              {project.gallery_images && project.gallery_images.length > 0 && (
                <AnimatedSection delay={0.1}>
                  <h3 className="text-lg font-semibold text-primary mb-4">Project Gallery</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {project.gallery_images.map((image: string, index: number) => (
                      <div
                        key={index}
                        className="aspect-square rounded-xl overflow-hidden cursor-pointer group"
                        onClick={() => openLightbox(index + 1)}
                      >
                        <img
                          src={image}
                          alt={`${project.title} - ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </AnimatedSection>
              )}

              <AnimatedSection delay={0.2}>
                <div className="bg-neutral-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                    <Ruler className="w-5 h-5 text-accent" />
                    Project Overview
                  </h2>
                  <div
                    className="rich-text-content text-neutral-600 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                  />
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div className="bg-neutral-50 rounded-2xl p-6 md:p-8">
                  <h2 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-accent" />
                    Project Details
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500">Client</p>
                        <p className="font-medium text-primary">{project.client}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500">Location</p>
                        <p className="font-medium text-primary">{project.location}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500">Year</p>
                        <p className="font-medium text-primary">{project.year}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm text-neutral-500">Status</p>
                        <p className="font-medium text-primary">{project.status}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {projectServices.length > 0 && (
                <AnimatedSection delay={0.4}>
                  <div className="bg-neutral-50 rounded-2xl p-6 md:p-8">
                    <h2 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-accent" />
                      Services Provided
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {projectServices.map((service) => (
                        <div
                          key={service}
                          className="flex items-center gap-3 rounded-xl bg-white border border-neutral-100 px-4 py-3"
                        >
                          <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                          <span className="text-sm font-medium text-primary">{service}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AnimatedSection>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              <AnimatedSection delay={0.1}>
                <div className="bg-primary rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-accent" />
                    Need a Survey?
                  </h3>
                  <p className="text-white/70 text-sm mb-6">
                    Contact us today to discuss your surveying needs and get a free quote.
                  </p>
                  <Link
                    to="/contact"
                    className="block w-full py-3 bg-accent text-white text-center rounded-full font-medium hover:bg-accent-dark transition-colors"
                  >
                    Get a Quote
                  </Link>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-white border border-neutral-100 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-primary mb-4">
                    Other Projects
                  </h3>
                  <div className="space-y-4">
                    {projects
                      .filter((p) => p.id.toString() !== id)
                      .slice(0, 4)
                      .map((p) => (
                        <Link
                          key={p.id}
                          to={`/projects/${p.id}`}
                          className="flex gap-3 group"
                        >
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                              src={p.image_url}
                              alt={p.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-primary text-sm line-clamp-2 group-hover:text-accent transition-colors">
                              {p.title}
                            </h4>
                            <p className="text-xs text-neutral-400 mt-1">
                              {p.category}
                            </p>
                          </div>
                        </Link>
                      ))}
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-neutral-50">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-neutral-600 mb-8 max-w-xl mx-auto">
              Let our team of expert surveyors help bring your vision to life with precision and professionalism.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent text-white rounded-full font-medium hover:bg-accent-dark transition-colors"
              >
                Contact Us
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary border border-neutral-200 rounded-full font-medium hover:bg-neutral-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                All Projects
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {allImages.length > 1 && (
            <>
              <button
                className="absolute left-4 p-2 text-white/70 hover:text-white bg-white/10 rounded-full"
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                className="absolute right-4 p-2 text-white/70 hover:text-white bg-white/10 rounded-full"
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          <div className="max-w-5xl max-h-[80vh] px-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={allImages[currentImageIndex]}
              alt={`${project.title} - ${currentImageIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-center text-white/70 text-sm mt-4">
              {currentImageIndex + 1} / {allImages.length}
            </p>
          </div>
        </motion.div>
      )}
    </main>
  );
}
